import { RouteState } from '@circuitry-ai/doc-data';
import { AuditLogsAgGridWrapper, ANALYST_AUDIT_LOG_STATIC_FILTERS, CtyButton, CustomTabs, PageHeader } from '@circuitry-ai/doc-ui';
import { Input } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AnalystTabs } from './analystBoard';
import AnalyticsList from './analyticsList';
import { Key, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AnalystHistory from './analysthistory';
import { NEW_GENIE_SESSION_STATE, useAnalystInsight } from './analystRuntime';

/** A tab the app adds to the Analysts page, after the built-in ones (e.g. Analyst Agent). */
export interface AnalystPageTab {
  readonly key: string;
  /** Translated by the page. */
  readonly title: string;
  readonly endContent?: ReactNode;
  /** The tab fills the page and scrolls inside itself, instead of the page scrolling. */
  readonly fillHeight?: boolean;
  readonly render: () => ReactNode;
}

/* eslint-disable-next-line */
export interface AnalystpageProps {
  readonly extraTabs?: readonly AnalystPageTab[];
}

enum AnalystPageTabs {
  ANALYST_LIST = 'list',
  ANALYST_HISTORY = 'analyst-history',
  AUDIT_LOGS = 'audit-logs',
}

const NO_EXTRA_TABS: readonly AnalystPageTab[] = [];

/**
 * The Analysts page. Where AI Insight is on (dev and staging builds) it has the Analyst Genie layout
 * (AnalystsHome); elsewhere (production builds) it is the classic page with tabs, unchanged.
 */
export function Analystpage({ extraTabs = NO_EXTRA_TABS }: AnalystpageProps) {
  const insight = useAnalystInsight();
  return insight ? <AnalystsHome extraTabs={extraTabs} /> : <ClassicAnalystpage extraTabs={extraTabs} />;
}

const createAnalystUrl = `/analytics/${RouteState.CREATE}?tab=${AnalystTabs.CONFIGURATION}`;

/**
 * The Analyst Genie layout. The list has a header with search, History, Audit Logs and Analyst Genie; each of
 * those opens its own page with a breadcrumb back to Analysts. The sections stay in ?tab= as before, so old
 * links (?tab=analyst-history, ?tab=audit-logs, ?tab=analyst-agent) open the same pages.
 */
function AnalystsHome({ extraTabs }: { readonly extraTabs: readonly AnalystPageTab[] }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const tabValue = searchParams.get('tab') ?? '';
  const extraTab = extraTabs.find((tab) => tab.key === tabValue);
  const genie = extraTabs[0];
  const open = (tab: string) => setSearchParams(tab === AnalystPageTabs.ANALYST_LIST ? {} : { tab });

  const historyButton = (
    <CtyButton variant="bordered" size="sm" startContent={<Icon icon="lucide:history" width={16} />} onPress={() => open(AnalystPageTabs.ANALYST_HISTORY)}>
      {t('History')}
    </CtyButton>
  );
  const auditButton = (
    <CtyButton variant="bordered" size="sm" startContent={<Icon icon="lucide:clipboard-list" width={16} />} onPress={() => open(AnalystPageTabs.AUDIT_LOGS)}>
      {t('Audit Logs')}
    </CtyButton>
  );

  if (extraTab) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <SectionHeader title={t('Analyst Genie')} chip={extraTab.endContent} onBack={() => open(AnalystPageTabs.ANALYST_LIST)} actions={<>{historyButton}{auditButton}</>} />
        {extraTab.fillHeight ? (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{extraTab.render()}</div>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto">{extraTab.render()}</div>
        )}
      </div>
    );
  }
  if (tabValue === AnalystPageTabs.ANALYST_HISTORY || tabValue === AnalystPageTabs.AUDIT_LOGS) {
    const history = tabValue === AnalystPageTabs.ANALYST_HISTORY;
    return (
      <div className="flex h-full min-h-0 flex-col">
        <SectionHeader title={history ? t('History') : t('Audit Logs')} onBack={() => open(AnalystPageTabs.ANALYST_LIST)} actions={history ? auditButton : historyButton} />
        <div className="min-h-0 flex-1 overflow-y-auto">
          {history ? <AnalystHistory /> : <AuditLogsAgGridWrapper staticFilters={ANALYST_AUDIT_LOG_STATIC_FILTERS} />}
        </div>
      </div>
    );
  }

  const actions = (
    <div className="flex flex-wrap items-center gap-2">
      <Input
        aria-label={t('Search analysts')}
        placeholder={t('Search analysts…')}
        size="sm"
        variant="bordered"
        className="w-[260px]"
        value={search}
        onValueChange={setSearch}
        isClearable
        onClear={() => setSearch('')}
        startContent={<Icon icon="lucide:search" width={15} className="text-default-400" />}
      />
      {historyButton}
      {auditButton}
      {genie ? (
        <CtyButton color="primary" size="sm" startContent={<Icon icon="lucide:sparkles" width={16} />} onPress={() => setSearchParams({ tab: genie.key }, { state: NEW_GENIE_SESSION_STATE })}>
          {t('Analyst Genie')}
        </CtyButton>
      ) : null}
    </div>
  );
  return (
    <div className="flex h-full min-h-0 flex-col">
      <PageHeader
        heading="Analysts"
        description={t('View and manage all the Analysts in your account.')}
        actionButton={actions}
        showCreateButton
        createButtonText={t('Create Analyst')}
        entityType="analyst"
        onCreateButtonClick={() => navigate(createAnalystUrl)}
      />
      <div className="min-h-0 flex-1 overflow-y-auto pb-4">
        <AnalyticsList variant="cards" search={search} />
      </div>
    </div>
  );
}

/** A page under Analysts: its title (and chip), a breadcrumb back, and actions on the right. */
function SectionHeader({ title, chip, onBack, actions }: {
  readonly title: string;
  readonly chip?: ReactNode;
  readonly onBack: () => void;
  readonly actions?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 pb-4">
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-bold leading-7 text-foreground">{title}</h1>
          {chip}
        </div>
        <nav aria-label={t('Breadcrumb')}>
          <ol className="flex items-center gap-1 text-xs text-default-500">
            <li>
              <button type="button" className="inline-flex items-center gap-1 hover:text-foreground" onClick={onBack}>
                <Icon icon="lucide:chart-no-axes-combined" width={13} />
                {t('Analysts')}
              </button>
            </li>
            <li aria-hidden="true"><Icon icon="lucide:chevron-right" width={12} /></li>
            <li aria-current="page" className="text-foreground">{title}</li>
          </ol>
        </nav>
      </div>
      <div className="flex flex-wrap items-center gap-2">{actions}</div>
    </div>
  );
}

/** The classic Analysts page: tabs for the list, history, audit logs and the app's extra tabs. */
function ClassicAnalystpage({ extraTabs }: { readonly extraTabs: readonly AnalystPageTab[] }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const analystPageTabs = [
    { key: AnalystPageTabs.ANALYST_LIST, title: t('All Analysts'), id: 'all-analyst-tab' },
    { key: AnalystPageTabs.ANALYST_HISTORY, title: t('Analyst History'), id: 'analyst-history-tab' },
    { key: AnalystPageTabs.AUDIT_LOGS, title: t('Audit Logs'), id: 'analyst-audit-logs-tab' },
    ...extraTabs.map((tab) => ({ key: tab.key, title: t(tab.title), id: `${tab.key}-tab`, endContent: tab.endContent })),
  ];

  // The tab lives in ?tab=, so links and the back button can open any tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabValue = searchParams.get('tab') ?? '';
  const currentTab = analystPageTabs.some((tab) => tab.key === tabValue) ? tabValue : AnalystPageTabs.ANALYST_LIST;
  const extraTab = extraTabs.find((tab) => tab.key === currentTab);

  const onTabChange = (tab: Key) => {
    // A tab's own parameters (e.g. the Analyst Agent's section) do not carry over to another tab.
    setSearchParams({ tab: String(tab) });
  };

  const renderSelectedTab = () => {
    switch (currentTab) {
      case AnalystPageTabs.ANALYST_LIST:
        return <AnalyticsList />;
      case AnalystPageTabs.ANALYST_HISTORY:
        return <AnalystHistory />;
      case AnalystPageTabs.AUDIT_LOGS:
        return (
            <AuditLogsAgGridWrapper staticFilters={ANALYST_AUDIT_LOG_STATIC_FILTERS} />
        );
      default:
        return null;
    }
  };

  return (
    <div className='flex flex-col h-full min-h-0'>
      <PageHeader
        heading="AInalysts"
        description={'View all your AInalysts reports at one place.'}
        showCreateButton={currentTab === AnalystPageTabs.ANALYST_LIST}
        createButtonText={'Create Analyst'}
        entityType="analyst"
        onCreateButtonClick={() => navigate(createAnalystUrl)}
      />
      <div className="advisor-tab-list mb-3">
        <CustomTabs
          tabs={analystPageTabs}
          selectedKey={currentTab}
          onTabChange={onTabChange}
        />
      </div>
      {extraTab?.fillHeight ? (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{extraTab.render()}</div>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto">{extraTab ? extraTab.render() : renderSelectedTab()}</div>
      )}
    </div>
  );
}

export default Analystpage;
